import { useContext, useEffect, useMemo, useState } from "react";
import toast from 'react-hot-toast';

import { formatDateToSydneyTimeZone } from "../../utils/dateUtils";

import { LabourerContext } from "../../contexts/LabourerContext";
import { Helmet } from "react-helmet";

import Select from 'react-select';
import SectionHeader from "../SectionHeader";
import Loader from "../Loader";
import ButtonLoader from "../ButtonLoader";
import Modal from "../Modal";
import TimeEntryForm from "../TimeEntries/TimeEntryForm";

import timeEntryService from "../../services/timeEntryService";
import projectService from "../../services/projectService";

const LabourerClockIn = () => {
  const { labourer } = useContext(LabourerContext);

  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isLoadingProjects, setIsLoadingProjects] = useState(null);


  const [ongoingTimeEntry, setOngoingTimeEntry] = useState(null);
  const [isLoadingOngoingTimeEntry, setIsLoadingOngoingTimeEntry] = useState(true);

  const [isSubmittingStartWorkDay, setIsSubmittingStartWorkDay] = useState(false);
  const [isSubmittingFinishWorkDay, setIsSubmittingFinishWorkDay] = useState(false);

  const [isTimeEntryFormOpen, setIsTimeEntryFormOpen] = useState(false);
  const [isSubmittingTimeEntry, setIsSubmittingTimeEntry] = useState(false);

  const fetchOngoingTimeEntry = async () => {
    const ongoingTimeEntry = await timeEntryService.fetchOngoingTimeEntry(labourer._id);

    setIsLoadingOngoingTimeEntry(false);
    setOngoingTimeEntry(ongoingTimeEntry);
  };

  const fetchProjects = async () => {
    setIsLoadingProjects(true);

    try {
      const { projects } = await projectService.getProjects({ page: 1, perPage: 999 });

      setProjects(projects);
    } catch (error) {
      console.log(error);
      toast.error('Something went wrong while loading projects. Please try again');
    } finally {
      setIsLoadingProjects(false);
    }
  }

  useEffect(() => {
    fetchOngoingTimeEntry();
    fetchProjects();
  }, [labourer]);

  const handleStartWorkday = async () => {
    if (!selectedProject) return toast.error('Please select a project before starting your workday');

    try {
      setIsSubmittingStartWorkDay(true);

      await timeEntryService.createTimeEntry({
        labourerId: labourer._id,
        projectId: selectedProject.value,
        startTime: new Date().toISOString(),
        type: 'work',
        entryType: 'auto'
      });

      await fetchOngoingTimeEntry();

      toast.success('Workday started');
    } catch (error) {
      console.log(error);
      toast.error(error?.response?.data?.message || 'Something went wrong. Please try starting your workday again');
    } finally {
      setIsSubmittingStartWorkDay(false);
    }
  };

  const handleFinishWorkday = async () => {
    try {
      setIsSubmittingFinishWorkDay(true);

      await timeEntryService.updateTimeEntry(ongoingTimeEntry._id, {
        endTime: new Date().toISOString(),
        entryType: 'auto'
      });

      setOngoingTimeEntry(null);
      toast.success('Workday finished. Enjoy the rest of your day')
    } catch (error) {
      console.log(error);
      toast.error(error?.response?.data?.message || 'Something went wrong. Please try finishing your workday again');
    } finally {
      setIsSubmittingFinishWorkDay(false);
    }
  }

  const handleTimeEntryFormSubmission = async (timeEntryData) => {
    try {
      setIsSubmittingTimeEntry(true);

      await timeEntryService.createTimeEntry({
        labourerId: labourer._id,
        projectId: timeEntryData?.projectId,
        startTime: timeEntryData?.startTime,
        endTime: timeEntryData?.endTime,
        entryType: 'manual',
        type: timeEntryData?.type
      });

      setIsTimeEntryFormOpen(false);
      toast.success('Time entry created');
    } catch (error) {
      console.log(error);
      toast.error(error?.response?.data?.message || 'Something went wrong. Please try again');
    } finally {
      setIsSubmittingTimeEntry(false);
    }
  }

  const projectSelectOptions = useMemo(() => {
    return projects.map(project => ({
      value: project._id,
      label: project.name
    }));
  }, [projects]);

  return (
    <>
      <Helmet>
        <title>Clock-In | ASE Group</title>
      </Helmet>

      <div className="border-ase-blue border rounded-lg h-fit flex-grow pb-1 max-w-[600px] mx-auto">
        <SectionHeader
          title="Clock-In"
          actionsLeft={(
            <div className="flex gap-2 items-center">
              <button
                onClick={() => setIsTimeEntryFormOpen(true)}
                className="px-2 py-1 bg-white rounded-md text-ase-blue font-semibold text-xs"
              >
                Manual Entry
              </button>
            </div>
          )}
        />

        <div className="p-4">
          {isLoadingOngoingTimeEntry && (
            <div className="flex flex-col h-[150px] w-full items-center justify-center">
              <Loader />
            </div>
          )}

          {!isLoadingOngoingTimeEntry && ongoingTimeEntry && (
            <div className="flex flex-col items-center">
              <p className="font-bold mb-2">
                Started work on {ongoingTimeEntry?.project?.name} at {formatDateToSydneyTimeZone(ongoingTimeEntry.startTime, 'dd.M.yyyy HH:mm')}.
              </p>

              <p className="text-center text-gray-600 font-bold text-sm mb-4">
                Press the "Finish" button to complete the clock-in.
              </p>

              <button
                className="bg-ase-blue h-[48px] w-full px-4 font-semibold text-white rounded-md flex items-center justify-center disabled:opacity-50"
                onClick={handleFinishWorkday}
                disabled={isSubmittingFinishWorkDay}
              >
                {isSubmittingFinishWorkDay && <ButtonLoader />}
                {!isSubmittingFinishWorkDay && <>Finish</>}
              </button>
            </div>
          )}

          {!isLoadingOngoingTimeEntry && !ongoingTimeEntry && (
            <div className="flex flex-col gap-4">
              <p className="text-center text-gray-600 font-bold text-sm">
                Select a project and press the "Start" button to clock-in.
              </p>

              <Select
                value={selectedProject}
                onChange={setSelectedProject}
                options={projectSelectOptions}
                isDisabled={isLoadingProjects}
                placeholder="Select project"
              />

              <button
                className="bg-ase-blue h-[48px] w-full px-4 font-semibold text-white rounded-md flex items-center justify-center disabled:opacity-50"
                onClick={handleStartWorkday}
                disabled={isSubmittingStartWorkDay}
              >
                {isSubmittingStartWorkDay && <ButtonLoader />}
                {!isSubmittingStartWorkDay && <>Start</>}
              </button>
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={isTimeEntryFormOpen}
      >
        <TimeEntryForm
          title="Add Time Entry"
          isSubmitting={isSubmittingTimeEntry}
          shouldShowLabourerField={false}
          projectSelectOptions={projectSelectOptions}
          onSubmit={handleTimeEntryFormSubmission}
          onCloseClicked={e => setIsTimeEntryFormOpen(false)}
        />
      </Modal>
    </>
  );
};

export default LabourerClockIn;
